import { Box, Button, Typography, useTheme } from "@mui/material";
import { NotificationsActiveOutlined as AlertsOnIcon } from "@mui/icons-material";
import BottomSheet from "../common/BottomSheet";
import { useAnalytics } from "../../hooks/useAnalytics";
import { hapticSelection } from "../../lib/haptics";
import { requestPushPermission } from "../../lib/push";
import { useRegisterPushToken } from "./hooks/useRegisterPushToken";
import { alertGold } from "../Dashboard/overviewPanel";

/**
 * Primes iOS's notification prompt, which follows this sheet's CTA. Same rules
 * as the ATT primer (ATT_PRIMER_COPY): never incentivise the answer, never gate
 * anything on it, and "Not now" costs nothing — alerts reach the inbox and
 * email either way. iOS shows its own prompt once per install, so this sheet
 * is only ever raised while that answer is still open (lib/push's
 * claimPushSoftAsk), and only once.
 */
export const PUSH_SOFT_ASK_COPY = {
  eyebrow: "Notifications",
  title: "Get insider alerts on your phone.",
  body: "Your alerts already reach your inbox and your email. Turn on notifications and they come to your phone as well, when insiders at the companies you follow buy or sell.\n\nApple will ask you to confirm on the next screen. You can change this at any time in Settings.",
  cta: "Turn on notifications",
  secondary: "Not now",
};

/** After a saved screen's first alert: screen alerts come to the phone by default. */
export const PUSH_SOFT_ASK_SCREENER_COPY = {
  ...PUSH_SOFT_ASK_COPY,
  title: "Get screen alerts on your phone.",
  body: "Your screen alerts already reach your inbox and your email. Turn on notifications and they come to your phone as well, when your saved screens have new matches.\n\nApple will ask you to confirm on the next screen. You can change this at any time in Settings.",
};

interface PushSoftAskSheetProps {
  open: boolean;
  onClose: () => void;
  /** Which alert raised it; the copy is insider-specific by default. */
  variant?: "insider" | "screener";
}

const PushSoftAskSheet = ({ open, onClose, variant = "insider" }: PushSoftAskSheetProps) => {
  const theme = useTheme();
  const copy = variant === "screener" ? PUSH_SOFT_ASK_SCREENER_COPY : PUSH_SOFT_ASK_COPY;
  const isDark = theme.palette.mode === "dark";
  const { capture } = useAnalytics();
  const { mutate: registerToken } = useRegisterPushToken();

  // The sheet closes first: iOS presents its prompt over the app, and the
  // answer arrives whenever the user gives it.
  const handleTurnOn = () => {
    hapticSelection();
    capture("push_soft_ask_answered", { choice: "turn_on" });
    onClose();
    void requestPushPermission().then((permission) => {
      capture("push_permission_result", { permission });
      if (permission === "granted") registerToken();
    });
  };

  const handleNotNow = () => {
    hapticSelection();
    capture("push_soft_ask_answered", { choice: "not_now" });
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={copy.eyebrow}>
      <Box sx={{ pt: 1 }}>
        {/* The alert gold, holding the same bell as "Alerts on" */}
        <Box
          aria-hidden
          sx={{
            width: 48,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "14px",
            color: alertGold(isDark).glyph,
            backgroundColor: isDark ? "rgba(234, 179, 8, 0.12)" : "rgba(161, 98, 7, 0.08)",
            border: `1px solid ${isDark ? "rgba(234, 179, 8, 0.3)" : "rgba(161, 98, 7, 0.25)"}`,
          }}
        >
          <AlertsOnIcon sx={{ fontSize: 24 }} />
        </Box>

        <Typography
          component="h2"
          sx={{
            mt: 2,
            fontWeight: 700,
            fontSize: "1.375rem",
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            color: theme.palette.text.primary,
          }}
        >
          {copy.title}
        </Typography>

        {/* Blank lines in the copy become real paragraph breaks. */}
        {copy.body.split("\n\n").map((paragraph) => (
          <Typography
            key={paragraph}
            sx={{
              mt: 1.5,
              fontSize: "0.9375rem",
              lineHeight: 1.55,
              color: theme.palette.text.secondary,
            }}
          >
            {paragraph}
          </Typography>
        ))}

        <Button
          fullWidth
          disableElevation
          variant="contained"
          onClick={handleTurnOn}
          sx={{
            mt: 3,
            py: 1.5,
            backgroundColor: "#22c55e",
            color: "#ffffff",
            fontWeight: 600,
            fontSize: "0.9375rem",
            textTransform: "none",
            borderRadius: 2,
            "&:hover": { backgroundColor: "#16a34a" },
            "&:active": { transform: "scale(0.98)" },
            "@media (prefers-reduced-motion: reduce)": { "&:active": { transform: "none" } },
          }}
        >
          {copy.cta}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={handleNotNow}
          sx={{
            mt: 0.5,
            py: 1.25,
            fontWeight: 600,
            fontSize: "0.875rem",
            textTransform: "none",
            borderRadius: 2,
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)",
            },
          }}
        >
          {copy.secondary}
        </Button>
      </Box>
    </BottomSheet>
  );
};

export default PushSoftAskSheet;
